import React, { useEffect, useState, useRef, Suspense } from "react";
import { motion } from "motion/react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useLocale } from "lib/hooks/useLocale";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useUserContext } from "lib/contexts/UserProvider";
import {
  initializeCheckout,
  hasAutoAppliedCoupon,
  getCheckoutExpiredParams,
  getTotalItems,
  redirectOnSuccessfulOrder,
  handleSuccessRedirection,
  getExitCouponNudgeConfig,
} from "lib/utils/checkout";
import CheckoutItems from "components/checkout/CheckoutItems";
import BillingDetails from "components/checkout/BillingDetails";
import Coupons from "components/checkout/Coupons";
import {
  inIframe,
  setPhoneNumberSpacing,
  isMobile,
  exitCheckout,
  roundStringNumber,
  setPageClarityTags,
  publishPostMessage,
  classNames,
} from "lib/utils/helpers";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import {
  analyticsEvents,
  analyticsTypes,
  C2P_ERROR_MESSAGES,
  constants,
  eventTypes,
  TOKEN_ERROR,
} from "lib/utils/constants";
import { Warning, ArrowLeft, CaretDown, ShoppingCartSimple } from "@phosphor-icons/react";
import GenericBannerStrip from "components/common/notification-strips/GenericBannerStrip";
import CheckoutExitPopup from "components/common/popups/CheckoutExitPopup";
import UrgencyNudgePopup from "components/common/popups/UrgencyNudgePopup";
import ExitCouponNudgePopup, { useFetchExitCoupon } from "components/common/popups/ExitCouponNudgePopup";
import DiscountNudgePopup from "components/common/popups/DiscountNudgePopup";
import { useApplyDiscount } from "lib/hooks/useApplyDiscount";
import Price from "components/common/Price";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import LogoutDialog from "components/checkout/dialogs/LogoutDialog";
import MarketingConsent from "components/common/MarketingConsent";
import SurveyPopup from "components/common/popups/SurveyPopUp";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import CheckoutItemsCount from "components/checkout/CheckoutItemsCount";
import CheckoutExpired from "components/checkout/CheckoutExpired";
import { ErrorScreen } from "components/error-handling/error-screen/ErrorScreen.tsx";
import { t } from "i18next";
import CustomError from "lib/core/customError.ts";
import { CheckoutErrorType, ErrorType } from "lib/types/error.ts";
import { AnimateExit } from "components/animation/AnimateExit";
import AuthenticatedViews from "components/layout/views/AuthenticatedViews";
import UnauthenticatedViews from "components/layout/views/UnauthenticatedViews";
import {
  OrderSummaryErrorCustom,
  OrderSummaryErrorHeader,
} from "components/error-handling/OrderSummaryError";
import { UnservicableProductDialog } from "components/checkout/dialogs/UnservicableProductDialog.tsx";
import { useCheckoutErrors } from "lib/hooks/useCheckoutErrors.ts";
import { useOrderSummary } from "lib/hooks/useOrderSummary";
import { ChannelName, CheckoutMessageTypes, createBroadcastChannel } from "lib/utils/broadcastChannel";
import CombinedProductsRemovalDialog from "components/checkout/dialogs/CombinedProductsRemovalDialog";
import { useAnalyticsInitialization } from "lib/hooks/useAnalyticsInitialization";
import { setUserOrderHistory } from "lib/utils/user";
import { useToken } from "lib/contexts/TokenProvider";
import { triggerSentryError } from "lib/utils/error";
import useAnonymousId from "lib/hooks/useAnonymousId";
import { CheckoutAccordion } from "@shopflo/ui/accordian/CheckoutAccordian";
import CustomComponentSlot from "components/common/CustomComponentSlot";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";
import PurchaseModeTabs from "components/subscriptions/PurchaseModeTabs";
import HideInSubscribe from "components/subscriptions/HideInSubscribe";

interface MainCheckoutProps {}

let isCheckoutInteractive: boolean = false;

const MainCheckoutV2: React.FC<MainCheckoutProps> = () => {
  const [searchParams] = useSearchParams();
  const referrer = searchParams.get("referrer") || undefined;
  const { t } = useLocale();

  const authContext = useAuthContext();
  const {
    state: { isAuthenticated, isAuthenticating },
    actions: { logout },
  } = authContext;

  const userContext = useUserContext();
  const {
    state: { user },
    actions: { setUserData, deleteAddressCache },
  } = userContext;
  const {
    state: { tokenId },
    actions: { setSegmentId },
  } = useToken();

  const checkoutContext = useCheckoutContext();
  const {
    state: {
      billing,
      appliedCoupons,
      checkoutView,
      isTwoStepCheckout,
      checkoutItems,
      isC2P,
      redirectUrl,
      isBuyNow,
      checkoutExpired,
      checkoutModal,
      showErrorScreen,
      checkoutLoading,
      availableAddOns,
      appliedCartAddOns,
      errorResponses,
      shippingHandles,
      exitSurveyOptions,
    },
    actions: {
      updateCheckoutBasedOnCheckoutResponse,
      setCheckoutView,
      setCheckoutExpired,
      setCheckoutModal,
      setShowErrorScreen,
      setCheckoutId,
    },
  } = checkoutContext;

  const merchantContext = useMerchantContext();
  const {
    state: { merchant, checkoutMetadata },
  } = merchantContext;
  const { layout } = checkoutMetadata?.uiAttributes ?? {};
  const exitCheckoutAction = checkoutMetadata?.uiAttributes?.exitCheckoutAction ?? "none";

  const orderSummaryStyle =
    checkoutMetadata?.default_view === "PAYMENTS" && merchant?.isOnePageCheckout
      ? "HEADER"
      : layout?.orderSummaryStyle ?? "TILE";

  const fetchAndSetExitCoupon = useFetchExitCoupon();

  const {
    isAddItemsWorthDelivery,
    isWeightBasedShipping,
    isItemNotServicable,
    triggerCheckoutErrorInfoToast,
  } = useCheckoutErrors();

  const analytics = useSendAnalyticsEvent();
  const { sendAnalyticsEvent, getLongSessionId } = analytics;
  const { initializeAnonymousId } = useAnonymousId();

  const longSessionId = getLongSessionId();

  const [uiState, setUiState] = useState({
    isOrderSummaryOpen: layout?.metadata?.isOrderSummaryOpen ?? false,
    isPayBtnLoading: true,
    openCouponPopup: false,
    showOrderSummary: true,
    showLogoutDialog: false,
    openConfirmClose: false,
  });

  const { hash: hashParam } = useLocation();

  const orderSummaryAccordionRef = useRef<any>(null);
  const totalItems = getTotalItems(checkoutItems);

  const { servicableItems, unservicableItems } = useOrderSummary();

  const { activePopup } = usePopupOrchestrator();
  const isDelhiveryOpen = activePopup?.key === "DELHIVERY_CONFIRMATION";

  const handleClose = () => {
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_BACK_CLICK,
      eventType: "click",
    });
    exitCheckout(redirectUrl, isBuyNow);
  };

  // Use the new analytics initialization hook
  useAnalyticsInitialization();

  useEffect(() => {
    initializeAnonymousId(isAuthenticated);
  }, [isAuthenticated]);

  useEffect(() => {
    setUiState((prev) => ({
      ...prev,
      isOrderSummaryOpen: layout?.metadata?.isOrderSummaryOpen ?? false,
    }));
  }, [layout?.metadata?.isOrderSummaryOpen]);

  useEffect(() => {
    if (checkoutView === "PAYMENTS") {
      if (orderSummaryAccordionRef?.current?.closeAccordion) {
        orderSummaryAccordionRef.current.closeAccordion();
      }
    }
    setPageClarityTags(checkoutView);
  }, [checkoutView]);

  useEffect(() => {
    if (inIframe()) {
      publishPostMessage(eventTypes.SET_CART_ITEM_COUNT, { totalItems });
    }
  }, [totalItems]);

  useEffect(() => {
    history.pushState({ checkout: "ongoing" }, "");
    publishPostMessage(eventTypes.CHECKOUT_READY, {});
  }, []);

  const addOnList = Object.values(availableAddOns ?? {});

  useEffect(() => {
    if (!addOnList.length) return;
    const isCartLevelAddOn = addOnList.some(
      (item) => item?.addon_level === "CART" && ["CHECKOUT", "DEFAULT"].includes(item?.ui_asset),
    );
    const isItemLevelAddOn = addOnList.some((item) => item?.addon_level === "ITEM");
    if (merchant?.merchantId && checkoutView === "PAYMENTS") {
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_ADDON_LOADED,
        eventFor: [analyticsTypes.SF_ANALYTICS],
        eventType: "load",
        metaData: {
          merchantId: merchant?.merchantId,
          addOnData: {
            view: "CHECKOUT",
            isCartLevelAddOn,
            isItemLevelAddOn,
          },
        },
      });
    }
  }, [addOnList?.length, merchant?.merchantId, checkoutView]);

  useEffect(() => {
    const backButtonHandler = async (event: PopStateEvent) => {
      const isSurveyInvalid =
        exitCheckoutAction === "none" ||
        (exitCheckoutAction === "survey" && !Boolean(exitSurveyOptions?.length > 0));

      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_BACK_CLICK,
        eventType: "click",
        metaData: {
          bannerData: {
            source: "BROWSER_BACK_BTN",
          },
        },
      });

      if (uiState.isOrderSummaryOpen) {
        setUiState((prev) => ({ ...prev, isOrderSummaryOpen: false }));
        history.pushState({ checkout: "ongoing" }, "");
        return;
      }
      // close current modal
      if (checkoutModal !== "NONE") {
        setCheckoutModal("NONE");
        history.pushState({ checkout: "ongoing" }, "");
        return;
      }

      if (checkoutMetadata?.initialCheckoutStep === "PAYMENTS") {
        if (isSurveyInvalid) {
          handleClose();
        } else {
          const hasCoupon = await fetchAndSetExitCoupon();
          if (hasCoupon === false) {
            handleClose();
          } else {
            setUiState((prev) => ({ ...prev, openConfirmClose: true }));
          }
        }
        return;
      }
      switch (checkoutView) {
        case "PAYMENTS":
          if (!isC2P) {
            setCheckoutView("ADDRESS_LIST");
          }
          break;
        default:
          if (isSurveyInvalid) {
            handleClose();
          } else {
            const hasCoupon = await fetchAndSetExitCoupon();
            if (hasCoupon === false) {
              handleClose();
            } else {
              setUiState((prev) => ({ ...prev, openConfirmClose: true }));
            }
          }
          return;
      }

      history.pushState({ checkout: "ongoing" }, "");
    };

    window.addEventListener("popstate", backButtonHandler);
    return () => {
      window.removeEventListener("popstate", backButtonHandler);
    };
  }, [
    merchant,
    checkoutView,
    checkoutModal,
    exitSurveyOptions,
    exitCheckoutAction,
    isC2P,
    uiState.isOrderSummaryOpen,
    fetchAndSetExitCoupon,
  ]);

  useEffect(() => {
    if (!tokenId || isAuthenticating) return;
    initialize();
  }, [tokenId, isAuthenticated, isAuthenticating]);

  useEffect(() => {
    if (isAddItemsWorthDelivery || isWeightBasedShipping) {
      triggerCheckoutErrorInfoToast();
    }
  }, [isAddItemsWorthDelivery, isWeightBasedShipping]);

  useEffect(() => {
    if (errorResponses?.length > 0) {
      sendAnalyticsEvent({
        eventName: analyticsEvents.SHIPPING_RATE_FAILURE,
        eventFor: [analyticsTypes.SF_ANALYTICS],
        eventType: "load",
        metaData: {
          errorSource: "shopflo",
          shippingData: shippingHandles,
          addressData: user?.default_shipping_address?.id,
          errorResponses,
        },
      });
    }
  }, [errorResponses?.length]);

  const initialize = async () => {
    try {
      const response = await initializeCheckout({
        tokenId: tokenId ?? null,
        checkoutIdParam: searchParams.get("checkoutId"),
        oldCheckoutIdParam: searchParams.get("old_checkout_id"),
        checkoutType: searchParams.get("checkout_type"),
        analytics,
        userContext,
        authContext,
        checkoutContext,
        merchantContext,
        isCheckoutInteractive,
        hashParam,
        referrer,
      });

      if (response !== undefined) {
        const { checkoutResponse, tokenData } = response;
        if (Boolean(checkoutResponse?.segment_id)) setSegmentId({ segmentId: checkoutResponse?.segment_id });

        // Handling for ACR case
        /**
          *   When order with a specific order is already placed
              API returns order details instead of other attributes
              We are displaying different screen for this case
          */
        if (Boolean(tokenData?.order?.id)) {
          handleSuccessRedirection({
            originUrl: merchant?.brandUrl ?? checkoutResponse?.metadata?.client_meta?.brand_url,
            orderStatusUrl: tokenData?.order?.order_status_url,
            tokenId: tokenData?.uid,
            checkoutId: searchParams.get("checkoutId"),
          });
          return;
        }

        handleCheckoutResponse(checkoutResponse);
        setUiState((prev) => ({ ...prev, isPayBtnLoading: false }));
        if (isAuthenticated) {
          setUserOrderHistory(
            checkoutResponse?.customer_id,
            checkoutResponse?.merchant_id,
            checkoutResponse?.uid,
          );
        }
      }
    } catch (error: any) {
      if (error instanceof CustomError) {
        handleCheckoutError(error);
      }
    }
  };

  const handleCheckoutError = (error: CustomError<CheckoutErrorType>) => {
    const { err } = error.extraData;

    if (err.name === "AbortError" || err.code === "ERR_CANCELED") {
      publishPostMessage(eventTypes.CHECKOUT_TIME_LIMIT_EXCEEDED, {});
      setShowErrorScreen(true);
      triggerSentryError(error, ErrorType.CHECKOUT_TIME_LIMIT_EXCEEDED);
    }

    // Logout when token expired
    else if (err?.message === "Token refresh failed" || err?.response?.status === 403) {
      logout();
    }
    // When order already placed move to order placement screen
    else if (TOKEN_ERROR.includes(err?.response?.data?.error)) {
      redirectOnSuccessfulOrder({ tokenId: searchParams.get("tokenId") });
      return;
    } else if (TOKEN_ERROR.includes(err?.response?.data?.error?.message)) {
      handleSuccessRedirection({
        orderStatusUrl: err?.response?.data?.error?.data?.order_url,
        checkoutId: searchParams.get("checkoutId"),
      });
      return;
    }
    // Displaying checkout error screen
    else if (
      err?.response?.status === 400 &&
      C2P_ERROR_MESSAGES.includes(err?.response?.data?.error?.message)
    ) {
      const { expired, isInvalidUser } = getCheckoutExpiredParams(err?.response?.data?.error?.message);
      setCheckoutExpired(expired, isInvalidUser);
    }
    // Network error (no server response) in iframe - fall back to Shopify native checkout
    else if (!err?.response && inIframe()) {
      publishPostMessage(eventTypes.CHECKOUT_TIME_LIMIT_EXCEEDED, {});
      triggerSentryError(error, ErrorType.CHECKOUT_TIME_LIMIT_EXCEEDED);
    } else {
      setShowErrorScreen(true);
    }
  };

  const handleCheckoutResponse = (checkoutResponse: any) => {
    if (layout?.availableElements) {
      setUiState((prev) => ({
        ...prev,
        showOrderSummary: Boolean(layout?.availableElements?.includes("ORDER_SUMMARY")),
      }));
    }

    // This is the same fresh response checkoutInit just dispatched, so its
    // item-level add-on eligibility is authoritative here too - trusting it
    // avoids clobbering what checkoutInit set with stale pre-init state.
    updateCheckoutBasedOnCheckoutResponse(checkoutResponse, "AUTO", true);
  };

  const closeCouponPopup = () => {
    setUiState((prev) => ({ ...prev, openCouponPopup: false }));
  };

  const handleLogout = () => {
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_LOGOUT_BTN_CLICKED,
      eventType: "click",
    });
    setCheckoutId("");
    setUserData({
      uid: "",
      email: "",
      phone: "",
      name: "",
      default_billing_address: undefined,
      default_shipping_address: undefined,
      addresses: undefined,
      hasHomeAddr: false,
      hasWorkAddr: false,
    });
    deleteAddressCache();
    setUiState((prev) => ({ ...prev, showLogoutDialog: false }));
    logout();

    // Broadcast Logout message to parallel iframes
    const channel = createBroadcastChannel(ChannelName.SHOPFLO_CHANNEL);
    channel.broadcast(CheckoutMessageTypes.USER_LOGGED_OUT, {});
  };

  const onOrderSummaryToggle = (isOpen: boolean) => {
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_ORDER_SUMMARY_BTN_CLICKED,
      eventType: "click",
      metaData: {
        metadata: {
          isOpen: isOpen,
        },
      },
    });
  };

  if (showErrorScreen) {
    return <ErrorScreen />;
  }

  if (checkoutExpired) {
    return (
      <div className={`relative w-full ${isMobile() ? "" : "rounded-[28px]"} h-full px-5`}>
        <CheckoutExpired isInvalidUser={checkoutExpired?.isInvalidUser} />
      </div>
    );
  }

  const getBannerStrip = () => {
    return checkoutMetadata?.metadataLoading ? (
      <div className="skeleton-loader sticky top-0 z-20 flex  min-h-[1.875rem]  w-full"></div>
    ) : (
      <GenericBannerStrip />
    );
  };

  const getOrderSummary = () => {
    return (
      <>
        <CheckoutAccordion
          customClass="bg-white border-base"
          id={"order-summary-section"}
          loading={checkoutLoading}
          ref={orderSummaryAccordionRef}
          defaultOpen={uiState.isOrderSummaryOpen}
          titleIcon={<ShoppingCartSimple className="h-4 w-4 text-coal-dark" strokeWidth={2.5} />}
          titleText={
            <div className="flex flex-row items-center">
              <p className="text-sm font-medium text-coal-dark">{t("order_summary")}</p>
              <OrderSummaryErrorHeader />
            </div>
          }
          subTitleText={
            <CheckoutItemsCount showOrderSummaryTotalTxt={layout?.metadata?.showOrderSummaryTotal ?? false} />
          }
          callbackOnToggle={(isOpen: boolean) => {
            onOrderSummaryToggle(isOpen);
            setUiState((prev) => ({ ...prev, isOrderSummaryOpen: isOpen }));
          }}
          content={
            <>
              {/* Line Items & Bill Summary */}
              <div className="flex w-full flex-col space-y-2">
                {Boolean(servicableItems) && (
                  <CheckoutItems
                    items={servicableItems ?? []}
                    disableOOSItems={false}
                    checkoutItemsMutable={layout?.itemsMutable}
                    availableAddOns={availableAddOns}
                    cartLevelAddOns={appliedCartAddOns}
                    enableAddOnActions={!isC2P}
                  />
                )}
                {(isAddItemsWorthDelivery || isWeightBasedShipping || isItemNotServicable) && (
                  <div className={`px-3 ${servicableItems.length === 0 ? "!mt-0" : "!mt-3"}`}>
                    <OrderSummaryErrorCustom />
                  </div>
                )}

                {Boolean(unservicableItems) && unservicableItems.length > 0 && (
                  <div className="px-3 pb-2">
                    <CheckoutItems
                      items={unservicableItems ?? []}
                      disableOOSItems={true}
                      checkoutItemsMutable={layout?.itemsMutable}
                      availableAddOns={availableAddOns}
                      cartLevelAddOns={appliedCartAddOns}
                      enableAddOnActions={!isC2P}
                      isUnservicable={true}
                    />
                  </div>
                )}
                {Boolean(billing) && servicableItems && servicableItems.length > 0 && (
                  <BillingDetails {...billing} />
                )}
              </div>
            </>
          }
        />
        <CustomComponentSlot position="ORDER_SUMMARY_SECTION" />
      </>
    );
  };

  const couponSkeleton = (
    <div className="px-3 py-4 mx-3 mt-3 space-y-2">
      <div className="skeleton-loader h-[16px] rounded-md w-2/5"></div>
      <div className="skeleton-loader h-[16px] rounded-md w-full"></div>
    </div>
  );

  const orderSummarySkeleton = (
    <div className="px-3 py-2 mx-3 mt-3 space-y-2">
      <div className="skeleton-loader h-[16px] rounded-md w-full"></div>
    </div>
  );

  const isCouponsVisible =
    (hasAutoAppliedCoupon(appliedCoupons) || merchant?.isDiscountEnabled) &&
    (checkoutMetadata?.initialCheckoutStep !== "PAYMENTS" || !isAuthenticated);

  return (
    <div className={`relative w-full ${isMobile() ? "" : "rounded-[28px]"}`}>
      <>
        <Header
          isCheckoutDataLoading={checkoutLoading}
          openConfirmClose={uiState.openConfirmClose}
          setOpenConfirmClose={(value) => setUiState((prev) => ({ ...prev, openConfirmClose: value }))}
          isOrderSummaryOpen={uiState.isOrderSummaryOpen}
          setIsOrderSummaryOpen={(value) => setUiState((prev) => ({ ...prev, isOrderSummaryOpen: value }))}
          onBeforeConfirmClose={fetchAndSetExitCoupon}
        />

        <motion.section
          animate={{ scale: isDelhiveryOpen ? 0.95 : 1 }}
          transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
          className={classNames(
            "scrollbar-hide w-full overflow-y-auto overflow-x-hidden md:h-[85vh]",
            checkoutView === "ADDRESS_LIST" ? "h-[calc(100dvh-9rem)]" : classNames("h-[calc(100dvh-4rem)]", inIframe() ? "" : "pb-16"),
          )}>
          <div className="sticky top-0 z-20">
            {getBannerStrip()}
            <PurchaseModeTabs />
          </div>
          <div className="mx-3">
            <CustomComponentSlot position="TOP_SECTION" />
          </div>
          <AnimateExit
            isChildVisible={uiState.showOrderSummary && orderSummaryStyle === "TILE"}
            loading={checkoutLoading}>
            <AnimateExit.Skeleton>{orderSummarySkeleton}</AnimateExit.Skeleton>
            <AnimateExit.Content>{getOrderSummary()}</AnimateExit.Content>
          </AnimateExit>
          <AnimateExit isChildVisible={Boolean(isCouponsVisible)} loading={checkoutLoading}>
            <AnimateExit.Skeleton>{couponSkeleton}</AnimateExit.Skeleton>
            <AnimateExit.Content>
              <HideInSubscribe>
                <div className="mt-3 px-3 ">
                  <Coupons />
                </div>
                <CustomComponentSlot position="COUPONS_SECTION" />
              </HideInSubscribe>
            </AnimateExit.Content>
          </AnimateExit>
          {isAuthenticated ? (
            <AuthenticatedViews isPrimaryBtnLoading={uiState.isPayBtnLoading} />
          ) : (
            <UnauthenticatedViews />
          )}
          {checkoutView === "ADDRESS_LIST" && <MarketingConsent />}

          {(isAuthenticated ||
            (checkoutView !== "ADDRESS_LIST" &&
              isTwoStepCheckout &&
              Boolean(user?.default_shipping_address?.phone))) && (
            <>
              <div
                className={`flex w-full flex-col items-start px-6 ${
                  checkoutView === "ADDRESS_LIST" ? "py-0 pb-[3.5rem] md:pb-[10.75rem]" : "py-2 pb-2"
                } w-full  space-y-2`}>
                <div className="flex w-full items-center justify-between">
                  <p className="cursor-pointer py-4 text-sm font-normal text-coal-light">
                    {t("logged_in_as")}
                    <span className="inline-block">
                      {setPhoneNumberSpacing(
                        isTwoStepCheckout ? user?.default_shipping_address?.phone : user?.phone,
                      )}
                    </span>
                  </p>
                  {!isC2P && (
                    <button
                      className="rounded-lg border border-gray-light bg-gray-lighter p-2 text-xs font-semibold text-coal-dark hover:border-gray-light hover:bg-gray-light"
                      id="flo_logout_btn"
                      onClick={() => {
                        sendAnalyticsEvent({
                          eventName: analyticsEvents.FLO_LOGOUT_CLICKED,
                          eventType: "click",
                        });
                        setUiState((prev) => ({ ...prev, showLogoutDialog: true }));
                      }}>
                      {t("logout")}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </motion.section>
      </>

      <GenericDialog
        isOpen={uiState.showLogoutDialog}
        translateAxis="y"
        modalType="NONE"
        dialogOverlay={true}
        customClass="overflow-scroll md:!top-auto md:absolute">
        <LogoutDialog
          setIsOpen={(value) => setUiState((prev) => ({ ...prev, showLogoutDialog: value }))}
          handleLogout={handleLogout}
        />
      </GenericDialog>

      <GenericDialog
        isOpen={checkoutModal === "COMBINED_PRODUCT_REMOVE"}
        setIsOpen={() => setCheckoutModal("NONE")}
        translateAxis="y"
        customClass="overflow-scroll md:!top-auto md:absolute"
        dialogOverlay={true}
        modalType="COMBINED_PRODUCT_REMOVE">
        <CombinedProductsRemovalDialog />
      </GenericDialog>

      <UnservicableProductDialog />
    </div>
  );
};

const Header: React.FC<{
  openConfirmClose: boolean;
  isCheckoutDataLoading: boolean;
  setOpenConfirmClose: (state: boolean) => void;
  isOrderSummaryOpen: boolean;
  setIsOrderSummaryOpen: (state: boolean) => void;
  onBeforeConfirmClose: () => Promise<boolean | null>;
}> = ({
  openConfirmClose,
  setOpenConfirmClose,
  isCheckoutDataLoading,
  isOrderSummaryOpen,
  setIsOrderSummaryOpen,
  onBeforeConfirmClose,
}) => {
  const {
    state: { merchant, checkoutMetadata },
  } = useMerchantContext();

  const {
    state: {
      redirectUrl,
      isBuyNow,
      isC2P,
      availableAddOns,
      appliedCartAddOns,
      checkoutView,
      billing,
      coupons,
      appliedCoupons,
      exitSurveyOptions,
      exitCouponNudgeRuntime,
      rawCheckoutObject,
    },
    actions: { setCheckoutView, setExitCouponNudgeRuntime },
  } = useCheckoutContext();

  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const { isAddItemsWorthDelivery, isWeightBasedShipping, isItemNotServicable } = useCheckoutErrors();
  const orderSummaryHeaderAccordionRef = useRef<any>(null);

  const [showSurvey, setShowSurvey] = useState(false);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const { applyDiscount } = useApplyDiscount();

  const { servicableItems, unservicableItems } = useOrderSummary();
  const { layout } = checkoutMetadata?.uiAttributes ?? {};
  const exitCheckoutAction = checkoutMetadata?.uiAttributes?.exitCheckoutAction ?? "none";
  const exitCouponNudgeConfig = getExitCouponNudgeConfig(
    checkoutMetadata?.uiAttributes?.exitCouponNudgeConfig,
    exitCouponNudgeRuntime,
  );

  const orderSummaryStyle =
    checkoutMetadata?.default_view === "PAYMENTS" && merchant?.isOnePageCheckout
      ? "HEADER"
      : layout?.orderSummaryStyle ?? "TILE";

  const handleClose = () => {
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_BACK_CLICK,
      eventType: "click",
    });
    exitCheckout(redirectUrl, isBuyNow);
  };
  const closePopup = () => {
    setOpenConfirmClose(false);
    history.pushState({ checkout: "ongoing" }, "");
  };

  const handleBackClick = async (clickEl: "BACK_BTN" | "LOGO") => {
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_BACK_CLICK,
      eventType: "click",
      metaData: {
        bannerData: {
          source: clickEl,
        },
      },
    });
    if (isOrderSummaryOpen) {
      setIsOrderSummaryOpen(false);
      return;
    }

    if (checkoutView === "PAYMENTS" && checkoutMetadata?.initialCheckoutStep !== "PAYMENTS" && !isC2P) {
      setCheckoutView("ADDRESS_LIST");
      return;
    }
    if (
      exitCheckoutAction === "none" ||
      (exitCheckoutAction === "survey" && !Boolean(exitSurveyOptions?.length > 0))
    ) {
      handleClose();
      return;
    }
    const hasCoupon = await onBeforeConfirmClose();
    if (hasCoupon === false) {
      handleClose();
      return;
    }
    setOpenConfirmClose(true);
  };

  const onOrderSummaryToggle = (isOpen: boolean) => {
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_ORDER_SUMMARY_BTN_CLICKED,
      eventType: "click",
      metaData: {
        metadata: {
          isOpen: isOpen,
        },
      },
    });
  };

  const getOrderSummary = () => {
    const orderSummaryEl =
      orderSummaryStyle === "HEADER" ? (
        <div className="flex flex-col">
          <div className="flex">
            {(isAddItemsWorthDelivery || isWeightBasedShipping || isItemNotServicable) && (
              <Warning className="h-4 w-4 text-ouch mb-1 mr-2" />
            )}
            <h2 className="mb-1 text-sm font-medium text-coal-light">{t("order_total") as string}</h2>
          </div>
          <CheckoutAccordion
            id={"order-summary-header"}
            ref={orderSummaryHeaderAccordionRef}
            defaultOpen={
              isOrderSummaryOpen || isAddItemsWorthDelivery || isWeightBasedShipping || isItemNotServicable
            }
            titleIcon={<></>}
            titleText={""}
            subTitleText={
              servicableItems &&
              servicableItems.length > 0 && (
                <h1 className="-mr-1 text-base font-medium text-coal-dark">
                  <Price
                    total={roundStringNumber(billing.total_payable) ?? ""}
                    compareAt={
                      Boolean(checkoutMetadata?.uiAttributes?.layout?.metadata?.showOriginalPrice)
                        ? billing.original_sub_total
                        : billing.sub_total
                    }
                    orientation="horizontal"
                  />
                </h1>
              )
            }
            callbackOnToggle={(isOpen: boolean) => {
              setIsOrderSummaryOpen(isOpen);
              onOrderSummaryToggle(isOpen);
            }}
            content={
              <>
                <div
                  className={`absolute left-0 top-0 flex ${
                    inIframe() ? "" : "md:h-[85vh]"
                  } h-screen w-full bg-black/50 md:w-[25rem]  ${
                    isMobile() ? "md:rounded-b-[28px]" : "md:rounded-[28px]"
                  }`}
                  onClick={() => {
                    orderSummaryHeaderAccordionRef.current.closeAccordion();
                    onOrderSummaryToggle(false);
                  }}
                />
                <div
                  className={`absolute left-0 top-0 flex w-full flex-col overflow-hidden rounded-b-[28px] border-gray-light ${
                    isMobile() ? "" : "md:rounded-[28px]"
                  } bg-white duration-200 ease-in-out`}>
                  <div
                    className={`flex h-16 w-full flex-row items-center justify-between bg-white px-4 shadow-sm ${
                      !inIframe() && "md:w-[25rem] md:rounded-t-[28px]"
                    } ${!isMobile() && "rounded-t-[28px]"}`}>
                    <div className="flex flex-row items-center">
                      <button className="flex cursor-pointer items-center justify-center">
                        <ArrowLeft
                          className="mr-2 h-6 w-6 text-coal-dark"
                          onClick={() => handleBackClick("BACK_BTN")}
                        />
                        <img
                          src={merchant?.logoUrl}
                          alt="Brand Logo"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                          className="float-left h-10 max-w-[9.375rem]	bg-transparent object-contain"
                          onClick={() => handleBackClick("LOGO")}
                        />
                      </button>
                    </div>
                    <button
                      className={`flex flex-col items-end justify-end cursor-pointer ${
                        isAddItemsWorthDelivery || isWeightBasedShipping || isItemNotServicable
                          ? "relative -right-2"
                          : ""
                      }`}
                      onClick={() => {
                        orderSummaryHeaderAccordionRef.current.closeAccordion();
                        onOrderSummaryToggle(false);
                      }}>
                      <div className="flex">
                        {(isAddItemsWorthDelivery || isWeightBasedShipping || isItemNotServicable) && (
                          <Warning className="h-4 w-4 text-ouch mb-1 mr-2" />
                        )}
                        <h2 className="mb-1 mr-2 w-fit text-sm font-medium text-coal-light">
                          {t("order_total") as string}
                        </h2>
                      </div>
                      <h1 className="inline-flex items-center space-x-1 text-base font-medium text-coal-dark">
                        <Price
                          total={roundStringNumber(billing.total_payable) ?? ""}
                          compareAt={
                            Boolean(checkoutMetadata?.uiAttributes?.layout?.metadata?.showOriginalPrice)
                              ? billing.original_sub_total
                              : billing.sub_total
                          }
                          orientation="horizontal"
                        />
                        <CaretDown className="h-4 w-4 rotate-180 cursor-pointer" />
                      </h1>
                    </button>
                  </div>
                  <GenericBannerStrip />
                  <div
                    className={`max-h-[9999px] overflow-hidden pt-2.5 transition-[max-height] duration-700 ease-in`}>
                    {/* Line Items & Bill Summary */}
                    <div className="flex flex-col space-y-2 bg-white">
                      {Boolean(servicableItems) && (
                        <div className="scrollbar-hide flex max-h-[20rem] overflow-scroll">
                          <CheckoutItems
                            items={servicableItems ?? []}
                            disableOOSItems={false}
                            checkoutItemsMutable={layout?.itemsMutable}
                            availableAddOns={availableAddOns}
                            cartLevelAddOns={appliedCartAddOns}
                            enableAddOnActions={!isC2P}
                          />
                        </div>
                      )}
                      {(isAddItemsWorthDelivery || isWeightBasedShipping || isItemNotServicable) && (
                        <div className={`px-3 ${servicableItems.length === 0 ? "!mt-0" : "!mt-3"}`}>
                          <OrderSummaryErrorCustom />
                        </div>
                      )}
                      {Boolean(unservicableItems.length) && (
                        <div className="scrollbar-hide flex max-h-[20rem] overflow-scroll px-3">
                          <CheckoutItems
                            items={unservicableItems ?? []}
                            disableOOSItems={true}
                            checkoutItemsMutable={layout?.itemsMutable}
                            availableAddOns={availableAddOns}
                            cartLevelAddOns={appliedCartAddOns}
                            enableAddOnActions={!isC2P}
                            isUnservicable={true}
                          />
                        </div>
                      )}
                      {Boolean(billing) && servicableItems && servicableItems.length > 0 && (
                        <BillingDetails {...billing} />
                      )}
                    </div>
                  </div>
                </div>
              </>
            }
            customClass="!border-[0px] !-mx-[18px] !mt-0 !w-[6rem] !justify-end"
            buttonClass={`!p-0 !h-full !w-[6rem] !justify-end ${
              isAddItemsWorthDelivery || isWeightBasedShipping || isItemNotServicable
                ? "relative -right-6"
                : ""
            }`}
            insideClass="!justify-end"
          />
        </div>
      ) : (
        <h1 className="text-base font-medium text-coal-dark">
          {servicableItems && servicableItems.length > 0 && (
            <Price
              total={billing.total_payable ?? ""}
              compareAt={
                Boolean(checkoutMetadata?.uiAttributes?.layout?.metadata?.showOriginalPrice)
                  ? billing.original_sub_total
                  : billing.sub_total
              }
            />
          )}
        </h1>
      );
    return isCheckoutDataLoading ? <div className="rounded skeleton-loader h-9 w-16"></div> : orderSummaryEl;
  };

  return (
    <header
      id={"main-header-section"}
      className={`relative z-30 flex h-16 w-full flex-row items-center justify-between bg-white px-6 shadow-sm ${
        !inIframe() && "md:w-[25rem] md:rounded-t-[28px]"
      } ${!isMobile() && "rounded-t-[28px]"}`}>
      <div className="flex flex-row items-center">
        <button className="flex cursor-pointer items-center justify-center">
          <ArrowLeft className="mr-2 h-6 w-6 text-coal-dark" onClick={() => handleBackClick("BACK_BTN")} />
          {checkoutMetadata?.metadataLoading ? (
            <div className="rounded skeleton-loader h-9 w-28"></div>
          ) : (
            <img
              src={checkoutMetadata?.uiAttributes?.logoUrl ?? ""}
              alt="Brand Logo"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              className="float-left h-10 max-w-[9.375rem]	bg-transparent object-contain"
              onClick={() => handleBackClick("LOGO")}
            />
          )}
        </button>
      </div>
      {getOrderSummary()}

      {exitCheckoutAction === "nudge" &&
        !(billing?.discount > 0) &&
        appliedCoupons.length === 0 &&
        (coupons as any[]).some(
          (c) =>
            c?.applicable &&
            !c?.coupon_details?.already_applied &&
            c?.coupon_details?.coupon_type !== "PREPAID" &&
            c?.coupon_details?.coupon_type !== "BXGY" &&
            c?.coupon_details?.coupon_type !== "CALLOUT_CARD" &&
            c?.coupon_details?.coupon_type !== "EXIT_COUPON",
        ) && (
          <DiscountNudgePopup closeCheckout={handleClose} isOpen={openConfirmClose} closePopup={closePopup} />
        )}
      {exitCheckoutAction === "nudge" &&
        (billing?.discount > 0 ||
          appliedCoupons.length > 0 ||
          !(coupons as any[]).some(
            (c) =>
              c?.applicable &&
              !c?.coupon_details?.already_applied &&
              c?.coupon_details?.coupon_type !== "PREPAID" &&
              c?.coupon_details?.coupon_type !== "BXGY" &&
              c?.coupon_details?.coupon_type !== "CALLOUT_CARD" &&
              c?.coupon_details?.coupon_type !== "EXIT_COUPON",
          )) &&
        (() => {
          const couponSavings = billing?.discount ?? 0;
          return (
            <CheckoutExitPopup
              closeCheckout={handleClose}
              isOpen={openConfirmClose}
              closePopup={closePopup}
              savingsAmount={couponSavings > 0 ? couponSavings : undefined}
            />
          );
        })()}
      {exitCheckoutAction === "survey" &&
        Boolean(exitSurveyOptions?.length > 0) &&
        (() => {
          const couponSavings = billing?.discount ?? 0;
          return (
            <SurveyPopup
              closeCheckout={handleClose}
              isOpen={openConfirmClose}
              closePopup={closePopup}
              savingsAmount={couponSavings > 0 ? couponSavings : undefined}
            />
          );
        })()}
      {exitCheckoutAction === "urgency_nudge" && (
        <UrgencyNudgePopup closeCheckout={handleClose} isOpen={openConfirmClose} closePopup={closePopup} />
      )}
      {exitCheckoutAction === "exit_coupon_nudge" && !exitCouponNudgeConfig && (
        <CheckoutExitPopup
          closeCheckout={handleClose}
          isOpen={openConfirmClose}
          closePopup={closePopup}
          savingsAmount={(billing?.discount ?? 0) > 0 ? billing?.discount : undefined}
        />
      )}
      {exitCheckoutAction === "exit_coupon_nudge" &&
        Boolean(exitCouponNudgeConfig) &&
        (() => {
          const nudgeCouponCode = exitCouponNudgeConfig?.couponCode?.trim()?.toUpperCase();
          const rawDiscounts: any[] = rawCheckoutObject?.discounts ?? [];
          const hasShopifyDiscount = rawDiscounts.some((c) => c?.coupon_metadata?.tags?.includes("SHOPIFY"));
          const isNudgeCouponAlreadyApplied =
            exitCouponNudgeConfig?.alreadyApplied ||
            (Boolean(nudgeCouponCode) &&
              (rawDiscounts.some((c) => c?.discount_code?.toUpperCase() === nudgeCouponCode) ||
                (appliedCoupons as any[]).some(
                  (c) =>
                    c?.coupon_code?.toUpperCase() === nudgeCouponCode ||
                    c?.discount_code?.toUpperCase() === nudgeCouponCode,
                )));
          if (hasShopifyDiscount) {
            return (
              <CheckoutExitPopup
                closeCheckout={handleClose}
                isOpen={openConfirmClose}
                closePopup={closePopup}
              />
            );
          }
          if (isNudgeCouponAlreadyApplied) {
            const savingsAmount = (billing?.discount ?? 0) > 0 ? billing?.discount : undefined;
            return (
              <CheckoutExitPopup
                closeCheckout={handleClose}
                isOpen={openConfirmClose}
                closePopup={closePopup}
                savingsAmount={savingsAmount}
              />
            );
          }
          return (
            <ExitCouponNudgePopup
              isOpen={openConfirmClose && !showSurvey}
              closePopup={closePopup}
              onLeaveAttempt={() => {
                if (exitCouponNudgeConfig?.additionalSurvey && exitSurveyOptions?.length) {
                  setShowSurvey(true);
                } else {
                  closePopup();
                  handleClose();
                }
              }}
            />
          );
        })()}
      {exitCheckoutAction === "exit_coupon_nudge" && Boolean(exitCouponNudgeConfig) && (
        <SurveyPopup
          isOpen={showSurvey}
          closePopup={() => {
            setShowSurvey(false);
            closePopup();
          }}
          closeCheckout={handleClose}
          onApplyAndStay={async () => {
            const couponCode = exitCouponNudgeConfig?.couponCode?.trim();
            setShowSurvey(false);
            if (couponCode) {
              setIsApplyingCoupon(true);
              await applyDiscount(couponCode);
              setIsApplyingCoupon(false);
            }
            setExitCouponNudgeRuntime(undefined);
            closePopup();
          }}
          isLoading={isApplyingCoupon}
        />
      )}
    </header>
  );
};

export default MainCheckoutV2;
